import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { fmtDate } from "@/components/UpcomingSessions";

const EMPTY = { name: "Capable 360 Group Training", date: "", time: "8:00 AM – 9:00 AM",
  location: "Capable 360 Studio", trainer: "Head Coach", focus: "Full Body Capability",
  difficulty: "All levels", duration: "55 min", capacity: 8, price: "Free", description: "", status: "published" };

export default function SessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [regs, setRegs] = useState([]);

  const load = () => api.get("/coach/sessions").then((r) => setSessions(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/coach/sessions", { ...form, capacity: Number(form.capacity) });
      toast.success("Session published");
      setShowForm(false); setForm(EMPTY); load();
    } catch (e2) { toast.error(fmtErr(e2)); }
  };

  const setStatus = async (id, status) => { await api.put(`/coach/sessions/${id}`, { status }); load(); };

  const openRegs = async (id) => {
    if (expanded === id) return setExpanded(null);
    const { data } = await api.get(`/coach/sessions/${id}/registrations`);
    setRegs(data);
    setExpanded(id);
  };

  const markAttended = async (r) => {
    await api.put(`/coach/registrations/${r.id}`, { attended: !r.attended });
    openRegsRefresh(r.session_id);
  };
  const openRegsRefresh = async (id) => {
    const { data } = await api.get(`/coach/sessions/${id}/registrations`);
    setRegs(data);
  };

  const F = ({ label, k, type = "text" }) => (
    <div>
      <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-1.5">{label}</p>
      <input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        className="w-full bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none [color-scheme:dark]"
        data-testid={`ses-form-${k}`} />
    </div>
  );

  return (
    <div data-testid="coach-sessions">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h2 className="font-display uppercase text-4xl">Group sessions <span className="text-zinc-600">({sessions.length})</span></h2>
        <button data-testid="session-create-open" onClick={() => setShowForm(!showForm)}
          className="bg-volt text-black text-xs uppercase tracking-[0.16em] font-extrabold px-5 py-3 hover:bg-white transition-colors">
          + New session
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} data-testid="session-create-form" className="border border-volt/40 bg-zinc-950 p-6 mb-6 grid sm:grid-cols-3 gap-4">
          <F label="Session name" k="name" /><F label="Date" k="date" type="date" /><F label="Time" k="time" />
          <F label="Location" k="location" /><F label="Trainer" k="trainer" /><F label="Focus" k="focus" />
          <F label="Difficulty" k="difficulty" /><F label="Duration" k="duration" /><F label="Capacity" k="capacity" type="number" />
          <F label="Price" k="price" />
          <div className="sm:col-span-2"><F label="Description" k="description" /></div>
          <div className="flex items-end">
            <button data-testid="session-create-submit" className="w-full bg-volt text-black text-xs uppercase tracking-[0.16em] font-extrabold py-3.5 hover:bg-white transition-colors">Publish session</button>
          </div>
        </form>
      )}

      <div className="border border-zinc-800 divide-y divide-zinc-800/70">
        {sessions.map((s) => (
          <div key={s.id} data-testid={`coach-session-${s.id}`} className="bg-zinc-950">
            <div className="flex flex-wrap items-center justify-between gap-3 p-5">
              <div>
                <p className="font-bold text-zinc-100">{s.name}</p>
                <p className="text-xs text-zinc-500 mt-1">{fmtDate(s.date)} · {s.time} · {s.location}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-zinc-400 uppercase tracking-[0.12em]">
                  <span className="text-volt font-display text-xl">{s.registered}</span>/{s.capacity} booked
                  {s.waitlisted > 0 && <span className="text-zinc-500"> · {s.waitlisted} waitlist</span>}
                </span>
                <span className={`text-[9px] uppercase tracking-[0.16em] font-extrabold px-2 py-1 ${
                  s.status === "published" ? "bg-volt text-black" : s.status === "full" ? "bg-zinc-700 text-white" : "bg-red-900/60 text-red-300"}`}>
                  {s.status}
                </span>
                <button onClick={() => openRegs(s.id)} data-testid={`session-participants-${s.id}`}
                  className="text-xs uppercase tracking-[0.14em] font-bold text-zinc-400 hover:text-volt transition-colors">
                  {expanded === s.id ? "Hide" : "Participants"}
                </button>
                {s.status !== "cancelled" ? (
                  <button onClick={() => setStatus(s.id, "cancelled")} data-testid={`session-cancel-${s.id}`}
                    className="text-xs uppercase tracking-[0.14em] font-bold text-zinc-600 hover:text-red-400 transition-colors">Cancel</button>
                ) : (
                  <button onClick={() => setStatus(s.id, "published")} data-testid={`session-republish-${s.id}`}
                    className="text-xs uppercase tracking-[0.14em] font-bold text-zinc-600 hover:text-volt transition-colors">Republish</button>
                )}
              </div>
            </div>
            {expanded === s.id && (
              <div className="border-t border-zinc-800/70 p-5">
                {!regs.length && <p className="text-sm text-zinc-600">No registrations yet.</p>}
                <div className="grid sm:grid-cols-2 gap-2">
                  {regs.map((r) => (
                    <label key={r.id} data-testid={`reg-row-${r.id}`}
                      className="flex items-center gap-3 border border-zinc-800 p-3 cursor-pointer hover:border-zinc-600">
                      <input type="checkbox" checked={r.attended} onChange={() => markAttended(r)}
                        data-testid={`reg-attended-${r.id}`} className="accent-[#ff5c1a] w-4 h-4" />
                      <div className="flex-1">
                        <p className="text-sm font-bold text-zinc-200">{r.name}</p>
                        <p className="text-xs text-zinc-500">{r.email} · {r.experience}{r.limitations ? ` · ⚠ ${r.limitations}` : ""}</p>
                      </div>
                      <span className={`text-[9px] uppercase tracking-[0.12em] font-bold px-2 py-1 ${r.status === "confirmed" ? "text-volt border border-volt/50" : "text-zinc-500 border border-zinc-700"}`}>
                        {r.status}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
